import { toVnDateCompact } from "@apv-erp/status";

export interface ImpositionSignature {
  key: string;
  label: string;
}

export interface ImpositionRoutingSignature extends ImpositionSignature {
  steps: string[];
}

export interface ImpositionCompatibilityResult {
  ok: boolean;
  code?: "ORDER_MISMATCH" | "MATERIAL_MISMATCH" | "LAMINATION_MISMATCH";
  reason?: string;
}

export function normalizeSignatureToken(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/\s+/g, " ");
}

export function extractMaterialSignature(detail: any): ImpositionSignature {
  if (detail?.materialSignature?.key && detail?.materialSignature?.label) {
    return {
      key: String(detail.materialSignature.key),
      label: String(detail.materialSignature.label),
    };
  }

  const product = detail?.product;
  const directMatId = detail?.materialId || product?.materialId || product?.material?.id || null;
  if (directMatId) {
    const label =
      detail?.material?.name ||
      detail?.materialName ||
      product?.material?.name ||
      product?.paperType ||
      String(directMatId);
    return { key: `MAT_ID:${directMatId}`, label };
  }

  const pmList: any[] = Array.isArray(product?.productMaterials)
    ? product.productMaterials.filter((pm: any) => !pm?.isDeleted)
    : [];
  if (pmList.length > 0) {
    const paperMat =
      pmList.find((pm: any) => {
        const groupText = normalizeSignatureToken(
          `${pm.material?.materialGroup || ""} ${pm.material?.type || ""} ${pm.material?.name || ""}`,
        );
        return (
          groupText.includes("giay") ||
          groupText.includes("paper") ||
          groupText.includes("ivory") ||
          groupText.includes("couche") ||
          groupText.includes("duplex") ||
          groupText.includes("kraft") ||
          groupText.includes("bristol") ||
          groupText.includes("decal")
        );
      }) || pmList[0];

    if (paperMat?.materialId || paperMat?.material?.id) {
      const mId = paperMat.materialId || paperMat.material.id;
      return {
        key: `MAT_ID:${mId}`,
        label: paperMat.material?.name || String(mId),
      };
    }
  }

  const textMat = String(
    detail?.materialName || product?.paperType || detail?.paperType || detail?.specifications || "",
  ).trim();
  if (textMat) {
    return {
      key: `MAT_TXT:${normalizeSignatureToken(textMat)}`,
      label: textMat,
    };
  }

  return { key: "UNKNOWN", label: "Chưa xác định chất liệu" };
}

export function extractRoutingSignature(detail: any): ImpositionRoutingSignature {
  if (detail?.routingSignature?.key) {
    return {
      key: String(detail.routingSignature.key),
      label: String(detail.routingSignature.label || "Chưa khai báo quy trình"),
      steps: Array.isArray(detail.routingSignature.steps) ? detail.routingSignature.steps : [],
    };
  }

  const rawProcesses: any[] = Array.isArray(detail?.product?.productProcesses)
    ? detail.product.productProcesses.filter((pp: any) => !pp?.isDeleted)
    : Array.isArray(detail?.productProcesses)
      ? detail.productProcesses.filter((pp: any) => !pp?.isDeleted)
      : [];

  if (rawProcesses.length > 0) {
    const sorted = [...rawProcesses].sort(
      (a, b) => Number(a.sortOrder ?? a.stepOrder ?? 0) - Number(b.sortOrder ?? b.stepOrder ?? 0),
    );
    const keys = sorted.map((pp) =>
      String(
        pp.process?.stepId ||
          pp.process?.id ||
          pp.processId ||
          normalizeSignatureToken(pp.process?.name || pp.stepName || ""),
      ),
    );
    const steps = sorted
      .map((pp) => String(pp.process?.name || pp.stepName || pp.process?.stepId || "").trim())
      .filter(Boolean);
    return {
      key: keys.join(">") || "NO_ROUTING",
      label: steps.join(" → ") || "Chưa khai báo quy trình",
      steps,
    };
  }

  const fallbackSteps: any[] = Array.isArray(detail?.routingSteps) ? detail.routingSteps : [];
  if (fallbackSteps.length > 0) {
    const sorted = [...fallbackSteps].sort(
      (a, b) => Number(a.stepOrder ?? a.sortOrder ?? 0) - Number(b.stepOrder ?? b.sortOrder ?? 0),
    );
    const keys = sorted.map((s) =>
      normalizeSignatureToken(s.stepCode || s.processId || s.stepName || s.name || ""),
    );
    const steps = sorted.map((s) => String(s.stepName || s.name || "").trim()).filter(Boolean);
    return {
      key: keys.join(">") || "NO_ROUTING",
      label: steps.join(" → ") || "Chưa khai báo quy trình",
      steps,
    };
  }

  return {
    key: "NO_ROUTING",
    label: "Chưa khai báo quy trình",
    steps: [],
  };
}

// Cùng quy tắc với resolveSurfaceFinishing ở máy chủ: cán màng phủ cả tờ in chung nên phải cùng kiểu.
const LAMINATION_LABELS: Record<string, string> = {
  LAMINATION_MATTE: "Cán màng mờ",
  LAMINATION_GLOSS: "Cán màng bóng",
  LAMINATION_GENERAL: "Cán màng",
};

export function extractLaminationType(detail: any): string {
  const procs: any[] = [
    ...(Array.isArray(detail?.productProcesses) ? detail.productProcesses : []),
    ...(Array.isArray(detail?.product?.productProcesses) ? detail.product.productProcesses : []),
  ].filter((pp) => !pp?.isDeleted);
  for (const pp of procs) {
    const sId = String(pp?.process?.stepId || pp?.processId || "").toUpperCase();
    const name = String(pp?.process?.name || "").toLowerCase();
    if (sId.includes("CAN-MO") || name.includes("màng mờ") || name.includes("cán mờ")) return "LAMINATION_MATTE";
    if (sId.includes("CAN-BONG") || name.includes("màng bóng") || name.includes("cán bóng")) return "LAMINATION_GLOSS";
    if (sId.includes("CAN") || name.includes("cán màng")) return "LAMINATION_GENERAL";
  }
  return "NONE";
}

export function resolveOrderKey(detail: any): string {
  return String(detail?.orderId || detail?.order?.id || "").trim();
}

export function resolveOrderCode(detail: any): string {
  return String(detail?.order?.orderId || detail?.orderCode || detail?.orderId || "").trim();
}

export function checkImpositionCompatibility(
  existingDetails: any[],
  candidateDetail: any,
): ImpositionCompatibilityResult {
  if (!existingDetails || existingDetails.length === 0) {
    return { ok: true };
  }

  const base = existingDetails[0];
  const baseOrderId = resolveOrderKey(base);
  const candOrderId = resolveOrderKey(candidateDetail);

  if (!baseOrderId || !candOrderId || baseOrderId !== candOrderId) {
    return {
      ok: false,
      code: "ORDER_MISMATCH",
      reason: `Khác đơn hàng (${resolveOrderCode(candidateDetail) || "N/A"} ≠ ${resolveOrderCode(base) || "N/A"}). Chỉ sản phẩm cùng đơn hàng mới được ghép chung 1 bài in.`,
    };
  }

  const baseMat = extractMaterialSignature(base);
  const candMat = extractMaterialSignature(candidateDetail);
  if (baseMat.key === "UNKNOWN" || candMat.key === "UNKNOWN" || baseMat.key !== candMat.key) {
    return {
      ok: false,
      code: "MATERIAL_MISMATCH",
      reason: `Khác chất liệu giấy (${candMat.label} ≠ ${baseMat.label}). Bình file ghép bài bắt buộc cùng chất liệu.`,
    };
  }

  const baseLam = extractLaminationType(base);
  const candLam = extractLaminationType(candidateDetail);
  if (baseLam !== "NONE" && candLam !== "NONE" && baseLam !== candLam) {
    return {
      ok: false,
      code: "LAMINATION_MISMATCH",
      reason: `Khác kiểu cán màng (${LAMINATION_LABELS[candLam]} ≠ ${LAMINATION_LABELS[baseLam]}). Cán màng phủ cả tờ in chung nên phải cùng kiểu.`,
    };
  }

  // Quy trình KHÔNG còn là điều kiện ghép (30/10/2026): in chung 1 tờ rồi mỗi sản phẩm đi công đoạn sau riêng;
  // người bình file / lập lệnh thiết lập lại quy trình, bù hao, số ups, số lượng ở lệnh sản xuất.
  return { ok: true };
}

export function validateBulkImpositionSelection(details: any[]): ImpositionCompatibilityResult {
  if (!details || details.length <= 1) {
    return { ok: true };
  }
  const base = details[0];
  for (let i = 1; i < details.length; i += 1) {
    const check = checkImpositionCompatibility([base], details[i]);
    if (!check.ok) {
      return check;
    }
  }
  return { ok: true };
}

export function resolveRemainingPrepressQty(detail: any, excludeLayoutId?: string | null): number {
  const quantity = Math.max(0, Number(detail?.quantity || 0));
  const plannedQty = Math.max(0, Number(detail?.plannedQty || 0));
  const rawImposed = Math.max(0, Number(detail?.imposedQty || 0));

  if (excludeLayoutId) {
    const layoutItems: any[] = Array.isArray(detail?.layoutItems) ? detail.layoutItems : [];
    const thisLayoutQty = layoutItems
      .filter((li) => !li?.isDeleted && li?.printLayoutId === excludeLayoutId)
      .reduce((sum, li) => sum + Math.max(0, Number(li?.plannedQty || 0)), 0);
    const otherImposed = Math.max(0, rawImposed - thisLayoutQty);
    return Math.max(0, quantity - Math.max(otherImposed, plannedQty));
  }

  if (typeof detail?.remainingPrepressQty === "number" && Number.isFinite(detail.remainingPrepressQty)) {
    return Math.max(0, detail.remainingPrepressQty);
  }

  if (detail?.isPrepressCompleted) {
    return 0;
  }

  const consumed = Math.max(rawImposed, plannedQty);
  return Math.max(0, quantity - consumed);
}

export function suggestSheetsPerUp(
  rows: Array<{ targetQty: number; ups: number }>,
): number {
  if (!rows || rows.length === 0) return 1;
  return rows.reduce((maxSheets, row) => {
    const qty = Math.max(1, Number(row.targetQty || 1));
    const ups = Math.max(1, Number(row.ups || 1));
    const needed = Math.ceil(qty / ups);
    return Math.max(maxSheets, needed);
  }, 1);
}

export function calculateRowPlannedQty(ups: number, sheetsPerUp: number): number {
  const safeUps = Math.max(1, Math.round(Number(ups || 1)));
  const safeSheets = Math.max(1, Math.round(Number(sheetsPerUp || 1)));
  return safeUps * safeSheets;
}

export function generateSuggestedLayoutCode(
  orderCode?: string | null,
  compactDate?: string,
): string {
  const datePart = compactDate || toVnDateCompact();
  const suffix = String(Math.floor(100 + Math.random() * 900));
  if (orderCode) {
    const cleaned = String(orderCode)
      .replace(/^(DH|SO|ORD)[-_]?/i, "")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(-6);
    if (cleaned) {
      return `BB-${cleaned}-${suffix}`;
    }
  }
  return `BB-${datePart}-${suffix}`;
}

export type DetailUrgencyKey = "OVERDUE" | "DUE_SOON" | "URGENT" | "EXPRESS" | "NORMAL";

export function computeDetailUrgency(
  detail: any,
  nowMs: number = Date.now(),
): DetailUrgencyKey {
  const priority = String(detail?.priority || detail?.order?.priority || "").toUpperCase();
  const deadlineRaw = detail?.deliveryDeadline || detail?.order?.deliveryDeadline || null;
  if (deadlineRaw) {
    const deadlineMs = new Date(deadlineRaw).getTime();
    if (!Number.isNaN(deadlineMs)) {
      if (deadlineMs < nowMs) return "OVERDUE";
      if (deadlineMs - nowMs <= 2 * 24 * 60 * 60 * 1000) return "DUE_SOON";
    }
  }
  if (priority === "URGENT" || priority === "EXPRESS") return "EXPRESS";
  if (priority === "HIGH") return "URGENT";
  return "NORMAL";
}
